<template>
  <v-container v-if="validPolicies.length > 0" class="pa-0" height="100%">
    <CardTemplate @show-modal="showDialog">
      <template #icon>
        <v-img
          class="account-img"
          src="./assets/update-comm.svg"
          alt="communications preferences"
          width="46px"
          height="46px"
        />
      </template>
      <template #title>
        {{ $t('L_set_communication_pre') }}
      </template>
      <template v-slot:content>
        <p
          :class="
            !$vuetify.display.xs
              ? 'd-flex align-center justify-center mt-4'
              : 'd-block text-left mt-0 ml-2 pb-4 pl-16'
          "
        >
          {{ $t('L_set_communication_post') }}
        </p>
      </template>
      <template #action>
        <OnexButton
          @click="showDialog"
          variant="text"
          id="update_paperless_trigger"
          :aria-label="$t('L_set_communication')"
        >
          {{ $t('L_edit') }}
        </OnexButton>
      </template>
    </CardTemplate>

    <MyAccountDialog
      :header="$t('L_set_communication')"
      :show="showUpdatePhoneDialog"
      :max-width="600"
      @save="save"
      @close="((showUpdatePhoneDialog = false), (closeIsActive = false))"
      :close-is-active="closeIsActive"
      :enable-save-button="enableSaveButton && !isSaveInProgress"
    >
      <v-row>
        <!-- With Online Only as your preferred... -->
        <v-col cols="12">
          <p>
            {{ $t('L_online_only_text') }}
            <v-tooltip
              open-on-click
              attach
              location="top"
              content-class="text-center pa-3"
              max-width="226"
            >
              <template #activator="{ props }">
                <v-icon
                  class="font-size--18 cursor-pointer"
                  color="info"
                  v-bind="props"
                  :icon="mdiHelpCircle"
                />
              </template>
              <span>{{ $t('L_paperless_tooltip') }}</span>
            </v-tooltip>
          </p>
        </v-col>
        <!-- Headers - Policy # | Online & Mail | Online Only -->
        <v-col cols="12">
          <div class="text-left text-body-2 divInlineBlock policyHeader">
            {{ $t('L_policy_num_acct_settings') }}
          </div>
          <div class="text-left text-body-2 divInlineBlock onlineAndMailHeader">
            {{ $t('Online_&_Mail') }}
          </div>
          <div class="text-left text-body-2 divInlineBlock onlineOnlyHeader">
            {{ $t('Online_Only') }}
          </div>
        </v-col>
        <!-- Table - Policy # | Online & Mail | Online Only -->
        <v-col cols="12">
          <v-container class="pa-2">
            <v-row
              v-for="(policy, index) in validPolicies"
              :key="index"
              :class="index % 2 === 0 ? 'backgroundLightGray' : 'backgroundWhite'"
            >
              <v-col
                cols="12"
                class="text-left divInlineBlock font-size--14 px-0 d-flex align-center"
              >
                <p class="pl-2 text-left text-body-2 divInlineBlock policyData">
                  <span>{{ policy.contractId }}</span>
                </p>
                <OnexRadioGroup
                  id="onex-radio-group_update_paperless_settings"
                  v-model="selections[policy.contractId]"
                  @update:modelValue="changed = true"
                  class="divInlineBlock"
                  label=" "
                  required
                >
                  <v-row>
                    <OnexRadio
                      class="radio-btn"
                      :name="'radio_' + policy.contractId"
                      value="MAIL"
                    />
                    <OnexRadio
                      class="ml-12 radio-btn"
                      :name="'radio_' + policy.contractId"
                      value="DIGITAL"
                    />
                  </v-row>
                </OnexRadioGroup>
              </v-col>
            </v-row>
          </v-container>
        </v-col>
        <!-- Changing your preference here... -->
        <v-col cols="12">
          {{ $t('L_paperless_footer') }}
          <p
            v-html="$t('L_paperless_disclosure', { disclosureLink: disclosureDocument })"
            class="pt-4"
          />
          <p class="pt-4 pb-4">
            {{ $t('L_paperless_notifications', { customerEmail: customerProfile.email }) }}
          </p>
        </v-col>
      </v-row>
    </MyAccountDialog>
    <v-dialog
      v-model="isConfirmationDialogOpen"
      :fullscreen="$vuetify.display.xs"
      max-width="600"
      z-index="10000"
    >
      <v-card class="update_user_mobile_modal">
        <v-card-title class="pb-0">
          <v-row class="text-right modal-primary-title">
            <v-col>
              <v-icon
                :size="$vuetify.display.xs ? '22' : '26'"
                @click="isConfirmationDialogOpen = false"
                class="btn-close"
                color="#333333"
                :icon="mdiClose"
              />
              <h2 class="text-h2 text-center pb-2" aria-live="assertive">
                {{ $t('L_paperless_communication_changes_saved') }}
              </h2>
            </v-col>
          </v-row>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col offset-md="2" md="8" offset="1" cols="10" class="text-center">
              <p>{{ $t('L_paperless_communication_updated') }}</p>
              <OnexButton
                block
                color="primary"
                class="mb-3 mt-5 px-2"
                @click="isConfirmationDialogOpen = false"
              >
                {{ $t('L_paperless_communication_ok') }}
              </OnexButton>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>
<script lang="ts">
import { defineComponent, type ComponentOptions } from 'vue';
import { useGlobalStore } from '@/stores/global/global.store';
import { usePolicyStore } from '@/modules/policy/store/policy.store';
import { mapState, mapStores } from 'pinia';
import { useCustomerStore } from '@/stores/customer/customer.store';
import { OnexButton, OnexRadioGroup, OnexRadio } from '@primerica/onex-ui-lib';
import bff from '@/shared/api/bff';
import MyAccountDialog from './MyAccountDialog.vue';
import CardTemplate from './CardTemplate.vue';
import { mdiClose, mdiHelpCircle } from '@mdi/js';
import type { CHANNELS } from '@/shared/models/constants.models.ts';

const componentOptions: ComponentOptions = {
  name: 'UpdateCommunication',
  components: {
    CardTemplate,
    MyAccountDialog,
    OnexButton,
    OnexRadioGroup,
    OnexRadio,
  },
  data: () => ({
    showUpdatePhoneDialog: false,
    closeIsActive: false,
    changed: false,
    headers: [
      {
        text: '',
        value: 'contractId',
      },
      {
        text: 'Online & Mail',
      },
      {
        text: 'Online Only',
      },
    ],
    selections: {} as Record<string, 'DIGITAL' | 'MAIL'>,
    preferences: [],
    isConfirmationDialogOpen: false,
    isSaveInProgress: false,
    mdiHelpCircle,
    mdiClose,
  }),
  computed: {
    ...mapStores(useGlobalStore, usePolicyStore),
    ...mapState(useCustomerStore, ['customerProfile']),
    ...mapState(usePolicyStore, ['policies']),
    ...mapState(useGlobalStore, {
      locale: 'getLocale',
    }),
    enableSaveButton() {
      return (
        Object.values(this.selections).every((value) => value === 'DIGITAL' || value === 'MAIL') &&
        this.changed
      );
    },
    validPolicies() {
      return this.policies.filter(
        (policy: { contractId: string | number; access: string }) =>
          +policy.contractId !== 0 && policy.access !== 'FULL'
      );
    },
    disclosureDocument() {
      let urlToShow;
      if (this.locale.indexOf('en_CA') !== -1) {
        urlToShow =
          'https://assets.contentstack.io/v3/assets/blt1dedeb682e8df2a2/blt38e4792640ee26f5/ecommunication_en_CA.pdf';
      } else if (this.locale.indexOf('fr_CA') !== -1) {
        urlToShow =
          'https://assets.contentstack.io/v3/assets/blt1dedeb682e8df2a2/bltf9257bbc10967b43/ecommunication_fr_CA.pdf';
      } else {
        urlToShow =
          'https://assets.contentstack.io/v3/assets/blt1dedeb682e8df2a2/blt56f08e892f0cc5b0/ecommunication_en_US.pdf';
      }
      return urlToShow;
    },
  },
  methods: {
    async showDialog() {
      if (this.validPolicies.length === 0) {
        return;
      }
      this.preferences = await bff.getCommunicationsPreferences();
      this.preferences.forEach((preference: { policyNumber: string; channel: CHANNELS }) => {
        this.selections[preference.policyNumber] = preference.channel;
      });
      this.showUpdatePhoneDialog = true;
    },
    async save() {
      try {
        this.isSaveInProgress = true;
        const changedSelections = {};
        this.preferences.forEach((preference: { policyNumber: string; channel: CHANNELS }) => {
          if (this.selections[preference.policyNumber] !== preference.channel) {
            (changedSelections as Record<string, CHANNELS>)[preference.policyNumber] =
              this.selections[preference.policyNumber];
          }
        });
        /*
         * Emit a custom action when a user successfully submits an update paperless settings request
         * https://www7.v1host.com/Primerica/attachment.img/284635/Analytics%20User%20Stories%20DT_3.30.21.docx
         */
        const i = this.$dynatrace.enterAction('account_settings_changes_saved_paperless_settings');
        await bff.updatePaperless(changedSelections, this.locale, true, 'AS');
        this.$dynatrace.leaveAction(i);
        this.isConfirmationDialogOpen = true;
        this.showUpdatePhoneDialog = false;
      } catch (e) {
        this.globalStore.addError({ error: e, context: 'myaccount' });
      } finally {
        this.isSaveInProgress = false;
      }
    },
  },
};

export default defineComponent(componentOptions);
</script>

<style lang="scss" scoped>
a[disabled] {
  color: rgba(0, 0, 0, 0.54) !important;
  cursor: default !important;
}

.policyHeader {
  width: 70px !important;
  margin-right: 20px;
}

.onlineAndMailHeader {
  width: 105px !important;
}

.onlineOnlyHeader {
  width: 105px !important;
}

.policyData {
  width: 105px !important;
  margin-right: 24px;
}

.divInlineBlock {
  display: inline-block;
}

.v-input {
  padding-bottom: 4px !important;
}

.backgroundWhite {
  background-color: #ffffff;
}

.backgroundLightGray {
  background-color: #d3d3d3;
}
</style>
<style lang="scss">
#onex-radio-group_update_paperless_settings {
  display: none !important;
}
</style>
